import { integrationModuleUrl } from "./design-mode";
import {
  integrationThemeCss,
  integrationOptionsSchema,
  type IntegrationOptions,
} from "./integration-options";
import {
  loadIntegrationPreset,
  registeredIntegrationPreset,
} from "./integration-presets";

/**
 * Applies an integration's theme and style preset inside its shadow root.
 * Gated presets (Propago) load from ESM-protected plugins; when an account
 * cannot load them the integration keeps the default style.
 */
export function applyIntegrationStyle(
  host: HTMLElement,
  input: Pick<IntegrationOptions, "theme" | "themePreset"> = {},
  dark = typeof matchMedia === "function" &&
    matchMedia("(prefers-color-scheme: dark)").matches,
) {
  const shadow = host.shadowRoot;
  if (!shadow) return;
  let theme: NonNullable<IntegrationOptions["theme"]>, preset: string;
  try {
    const parsed = integrationOptionsSchema
      .pick({ theme: true, themePreset: true })
      .parse({ theme: input.theme, themePreset: input.themePreset });
    theme = parsed.theme;
    preset = parsed.themePreset;
  } catch {
    theme = {};
    preset = "default";
  }
  const mount = shadow.querySelector<HTMLElement>("div");
  if (preset !== "default" && !registeredIntegrationPreset(preset))
    void loadIntegrationPreset(preset, (module) =>
      integrationModuleUrl({ localName: `proof-${module}` }),
    ).then((loaded) => {
      if (loaded) applyIntegrationStyle(host, input, dark);
      else mount?.setAttribute("data-theme-preset-unavailable", preset);
    });
  let style = shadow.querySelector<HTMLStyleElement>(
    "style[data-integration-theme]",
  );
  if (!style) {
    style = document.createElement("style");
    style.dataset.integrationTheme = "";
    shadow.append(style);
  }
  const css = integrationThemeCss(theme, preset as never);
  if (style.textContent !== css) style.textContent = css;
  if (!mount) return;
  mount.classList.add("proof-integration");
  mount.setAttribute("data-theme-preset", preset);
  const mode = theme.mode || "system";
  mount.dataset.theme = mode === "system" ? (dark ? "dark" : "light") : mode;
}
